/**
 * Enhanced Auto Evidence Cropper Modal
 *
 * PDF flow: page selection → per-page crop → export selected pages only.
 * Image/HEIC: single-page crop (full page default).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Loader2, Check, Crop, Scissors, RotateCw, RotateCcw, RefreshCcw, Maximize,
  AlertCircle, ToggleRight, ToggleLeft, ChevronLeft, ChevronRight, FileText, Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getAttachmentFileType, createCroppedUploadFile, wrapUploadFileWithMime } from '../../utils/attachmentHelpers';
import { renderPdfPageToCanvas, getPdfPageCount } from '../../utils/pdfRenderer';
import { exportCroppedImageBlob, combineCanvasesVertically, CROP_EXPORT_JPEG_QUALITY } from '../../utils/cropImageExport';
import { convertHeicToJpeg } from '../../utils/heicConverter';
import {
  parsePdfPageRange,
  isCropAreaUnusuallySmall,
  formatPageList,
} from '../../utils/pdfPageSelection';
import {
  FULL_PAGE_CROP,
  cropSettingsFromMetadata,
  normalizeCropArea,
  applyCropResize,
  applyCropResizeFromPointer,
  buildCropOverlayPanels,
  computeFittedDisplaySize,
  getReferencePageInches,
  getStageDimensions,
} from '../../utils/cropEditorUtils';

const INCHES_PER_CM = 0.393701;
const CM_PER_INCH = 2.54;

const EnhancedAutoCropperModal = ({
  isOpen,
  onClose,
  uploadedFile,
  onCropComplete,
  onSkipCrop,
  documentCategory,
  initialCropMetadata = null,
  isRecrop = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [sourceFileType, setSourceFileType] = useState('image');
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  /** JPEG (or original) file passed to upload pipeline — never raw HEIC after conversion. */
  const [uploadSourceFile, setUploadSourceFile] = useState(null);

  const [pdfTotalPages, setPdfTotalPages] = useState(1);
  const [pdfRenderedDimensions, setPdfRenderedDimensions] = useState({ width: 0, height: 0 });

  const [workflowPhase, setWorkflowPhase] = useState('cropping');
  const [customPageRange, setCustomPageRange] = useState('');
  const [selectedPages, setSelectedPages] = useState([]);
  const [pdfThumbnails, setPdfThumbnails] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState(false);
  const [pageSelectionError, setPageSelectionError] = useState('');
  const [currentCropIndex, setCurrentCropIndex] = useState(0);
  const [pageCropSettings, setPageCropSettings] = useState({});

  const [cropping, setCropping] = useState(false);
  const [cropArea, setCropArea] = useState(FULL_PAGE_CROP);
  const [rotation, setRotation] = useState(0);
  const [straighten, setStraighten] = useState(0);
  const [saveForLater, setSaveForLater] = useState(true);
  const [showSmallCropConfirm, setShowSmallCropConfirm] = useState(false);
  const [pendingCropAction, setPendingCropAction] = useState(null);

  const [dragging, setDragging] = useState(null);
  const [cropUnit, setCropUnit] = useState('inch');
  const [manualDimensions, setManualDimensions] = useState({
    top: 0, left: 0, width: 0, height: 0, bottom: 0, right: 0,
  });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0, height: 0, naturalWidth: 0, naturalHeight: 0,
  });
  const [baseDisplaySize, setBaseDisplaySize] = useState({ width: 0, height: 0 });
  const [dimensionWarning, setDimensionWarning] = useState('');

  const imageRef = useRef(null);
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const cropBoxRef = useRef(null);
  const pendingInitialCropRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const isIdCategory = documentCategory === 'government_id';
  const isPdf = sourceFileType === 'pdf';
  const currentPageNumber = selectedPages[currentCropIndex] || 1;
  const selectedPageCount = selectedPages.length;

  const resetCropState = useCallback(() => {
    setCropArea(FULL_PAGE_CROP);
    setRotation(0);
    setStraighten(0);
    setBaseDisplaySize({ width: 0, height: 0 });
    setDimensionWarning('');
  }, []);

  const loadPdfThumbnails = useCallback(async (file, totalPages) => {
    setThumbnailsLoading(true);
    const thumbs = {};
    try {
      const first = await renderPdfPageToCanvas(file, 1, 0.35);
      thumbs[1] = first.imageUrl;
      setPdfThumbnails(thumbs);

      const initialBatch = Math.min(totalPages, 6);
      for (let page = 2; page <= initialBatch; page += 1) {
        const result = await renderPdfPageToCanvas(file, page, 0.35);
        thumbs[page] = result.imageUrl;
        setPdfThumbnails((prev) => ({ ...prev, [page]: result.imageUrl }));
      }
    } catch (err) {
      console.warn('[CROPPER] Thumbnail generation failed:', err);
    } finally {
      setThumbnailsLoading(false);
    }
  }, []);

  const ensurePdfThumbnail = useCallback(async (page) => {
    if (!uploadedFile || pdfThumbnails[page]) return;
    try {
      const result = await renderPdfPageToCanvas(uploadedFile, page, 0.35);
      setPdfThumbnails((prev) => ({ ...prev, [page]: result.imageUrl }));
    } catch (err) {
      console.warn(`[CROPPER] Thumbnail for page ${page} failed:`, err);
    }
  }, [uploadedFile, pdfThumbnails]);

  const renderPdfPage = useCallback(async (pageNumber) => {
    if (!uploadedFile) return;
    setLoading(true);
    try {
      const result = await renderPdfPageToCanvas(uploadedFile, pageNumber, 2);
      setProcessedImageUrl(result.imageUrl);
      setPdfRenderedDimensions({ width: result.renderedWidth, height: result.renderedHeight });
    } catch (err) {
      toast.error('Failed to load PDF page');
    } finally {
      setLoading(false);
    }
  }, [uploadedFile]);

  useEffect(() => {
    if (isOpen && initialCropMetadata) {
      pendingInitialCropRef.current = cropSettingsFromMetadata(initialCropMetadata);
    } else {
      pendingInitialCropRef.current = null;
    }
  }, [isOpen, initialCropMetadata]);

  useEffect(() => {
    if (!uploadedFile || !isOpen) {
      setProcessedImageUrl(null);
      setOriginalFileUrl(null);
      return undefined;
    }

    const processFile = async () => {
      setLoading(true);
      setLoadError(null);
      setWorkflowPhase('cropping');
      setCustomPageRange('');
      setSelectedPages([]);
      setPageSelectionError('');
      setCurrentCropIndex(0);
      setPageCropSettings({});
      setShowSmallCropConfirm(false);
      resetCropState();
      setUploadSourceFile(null);

      try {
        const fileType = getAttachmentFileType(uploadedFile);
        setSourceFileType(fileType);
        const origUrl = URL.createObjectURL(uploadedFile);
        setOriginalFileUrl(origUrl);

        if (fileType === 'pdf') {
          setUploadSourceFile(uploadedFile);
          const pageCount = await getPdfPageCount(uploadedFile);
          setPdfTotalPages(pageCount);
          setSelectedPages([]);
          setWorkflowPhase('pageSelection');
          void loadPdfThumbnails(uploadedFile, pageCount);
        } else if (fileType === 'heic') {
          try {
            const converted = await convertHeicToJpeg(uploadedFile);
            setProcessedImageUrl(converted.url);
            setUploadSourceFile(
              new File([converted.blob], converted.originalName, { type: 'image/jpeg' })
            );
            setSelectedPages([1]);
          } catch {
            setLoadError('This HEIC image could not be converted. Please try saving it as JPG or PNG and upload again.');
            return;
          }
        } else if (fileType === 'image') {
          const normalized = wrapUploadFileWithMime(uploadedFile);
          setUploadSourceFile(normalized);
          setProcessedImageUrl(URL.createObjectURL(normalized));
          setSelectedPages([1]);
        } else {
          setLoadError('Unsupported file type. Please upload PNG, JPG, PDF, or HEIC.');
          return;
        }
      } catch (err) {
        setLoadError(err.message || 'Failed to process file');
      } finally {
        setLoading(false);
      }
    };

    processFile();
    return () => {
      if (originalFileUrl) URL.revokeObjectURL(originalFileUrl);
    };
  }, [uploadedFile, isOpen, loadPdfThumbnails, resetCropState]);

  const applyPageCropSettings = useCallback((pageNumber) => {
    const saved = pageCropSettings[pageNumber];
    if (saved) {
      setCropArea(saved.cropArea || FULL_PAGE_CROP);
      setRotation(saved.rotation || 0);
      setStraighten(saved.straighten || 0);
    } else {
      resetCropState();
    }
  }, [pageCropSettings, resetCropState]);

  const saveCurrentPageCropSettings = useCallback(() => {
    const page = currentPageNumber;
    setPageCropSettings((prev) => ({
      ...prev,
      [page]: {
        cropArea: { ...cropArea },
        rotation,
        straighten,
        isFullPage:
          cropArea.x === 0 &&
          cropArea.y === 0 &&
          cropArea.width === 100 &&
          cropArea.height === 100,
      },
    }));
  }, [currentPageNumber, cropArea, rotation, straighten]);

  const handleContinueToCrop = async () => {
    let pages = selectedPages;
    if (customPageRange.trim()) {
      const parsed = parsePdfPageRange(customPageRange, pdfTotalPages);
      if (parsed.error) {
        setPageSelectionError(parsed.error);
        return;
      }
      pages = parsed.pages;
    }
    if (!pages.length) {
      setPageSelectionError('Select at least one page to continue.');
      return;
    }
    setPageSelectionError('');
    setSelectedPages(pages);
    setCurrentCropIndex(0);
    setPageCropSettings({});
    resetCropState();
    setWorkflowPhase('cropping');
    await renderPdfPage(pages[0]);
  };

  const toggleThumbnailPage = (page) => {
    setSelectedPages((prev) => {
      const set = new Set(prev);
      if (set.has(page)) set.delete(page);
      else set.add(page);
      return Array.from(set).sort((a, b) => a - b);
    });
    setPageSelectionError('');
  };

  const applyCustomPageRange = (value) => {
    if (!value.trim()) return;
    const parsed = parsePdfPageRange(value, pdfTotalPages);
    if (!parsed.error) {
      setSelectedPages(parsed.pages);
      setPageSelectionError('');
    }
  };

  const handleCustomPageRangeChange = (e) => {
    const value = e.target.value;
    setCustomPageRange(value);
    applyCustomPageRange(value);
  };

  const handleCustomRangeBlur = () => {
    applyCustomPageRange(customPageRange);
  };

  const handleApplyCropToAll = () => {
    const template = {
      cropArea: { ...cropArea },
      rotation,
      straighten,
      isFullPage:
        cropArea.x === 0 && cropArea.y === 0 && cropArea.width === 100 && cropArea.height === 100,
    };
    const next = {};
    selectedPages.forEach((page) => {
      next[page] = { ...template, cropArea: { ...template.cropArea } };
    });
    setPageCropSettings(next);
    toast.success('Crop applied to all selected pages');
  };

  const handleUseFullPage = () => {
    setCropArea({ ...FULL_PAGE_CROP });
    setDimensionWarning('');
  };

  const handleResetCrop = () => {
    setCropArea({ ...FULL_PAGE_CROP });
    setDimensionWarning('');
    toast.info('Crop reset to full image');
  };

  const clampDimension = (value, min, max) => Math.max(min, Math.min(max, value));

  const syncStageDimensions = useCallback((baseSize, rotationDegrees, naturalWidth, naturalHeight) => {
    const { stageWidth, stageHeight } = getStageDimensions(
      baseSize.width,
      baseSize.height,
      rotationDegrees,
    );
    setImageDimensions({
      width: stageWidth,
      height: stageHeight,
      naturalWidth,
      naturalHeight,
    });
  }, []);

  const updateManualDimensionsFromCropArea = useCallback(() => {
    if (imageDimensions.naturalWidth === 0) return;
    const { pageWidthInches, pageHeightInches } = getReferencePageInches(rotation);
    const topInches = (cropArea.y / 100) * pageHeightInches;
    const leftInches = (cropArea.x / 100) * pageWidthInches;
    const widthInches = (cropArea.width / 100) * pageWidthInches;
    const heightInches = (cropArea.height / 100) * pageHeightInches;
    const bottomInches = pageHeightInches - topInches - heightInches;
    const rightInches = pageWidthInches - leftInches - widthInches;
    const toUnit = (inches) => (cropUnit === 'cm' ? inches * CM_PER_INCH : inches);
    setManualDimensions({
      top: Math.max(0, toUnit(topInches)).toFixed(2),
      left: Math.max(0, toUnit(leftInches)).toFixed(2),
      width: Math.max(0, toUnit(widthInches)).toFixed(2),
      height: Math.max(0, toUnit(heightInches)).toFixed(2),
      bottom: Math.max(0, toUnit(bottomInches)).toFixed(2),
      right: Math.max(0, toUnit(rightInches)).toFixed(2),
    });
  }, [cropArea, cropUnit, imageDimensions.naturalWidth, rotation]);

  const handleManualDimensionChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const { pageWidthInches, pageHeightInches } = getReferencePageInches(rotation);
    const inchValue = cropUnit === 'cm' ? numValue * INCHES_PER_CM : numValue;
    let newCropArea = { ...cropArea };
    let warning = '';
    switch (field) {
      case 'top': {
        const topPercent = (inchValue / pageHeightInches) * 100;
        if (topPercent < 0 || topPercent > 100 - newCropArea.height) warning = 'Value out of bounds';
        newCropArea.y = clampDimension(topPercent, 0, 100 - newCropArea.height);
        break;
      }
      case 'left': {
        const leftPercent = (inchValue / pageWidthInches) * 100;
        if (leftPercent < 0 || leftPercent > 100 - newCropArea.width) warning = 'Value out of bounds';
        newCropArea.x = clampDimension(leftPercent, 0, 100 - newCropArea.width);
        break;
      }
      case 'width': {
        const widthPercent = (inchValue / pageWidthInches) * 100;
        if (widthPercent < 10 || newCropArea.x + widthPercent > 100) warning = 'Width out of bounds';
        newCropArea.width = clampDimension(widthPercent, 10, 100 - newCropArea.x);
        break;
      }
      case 'height': {
        const heightPercent = (inchValue / pageHeightInches) * 100;
        if (heightPercent < 10 || newCropArea.y + heightPercent > 100) warning = 'Height out of bounds';
        newCropArea.height = clampDimension(heightPercent, 10, 100 - newCropArea.y);
        break;
      }
      default:
        break;
    }
    setDimensionWarning(warning);
    setCropArea(newCropArea);
    setManualDimensions((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageLoad = () => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    const containerWidth = containerRef.current?.clientWidth || 600;
    const maxWidth = Math.max(containerWidth - 32, 200);
    const maxHeight = Math.min(typeof window !== 'undefined' ? window.innerHeight * 0.7 : 560, 560);
    const baseSize = computeFittedDisplaySize(img.naturalWidth, img.naturalHeight, maxWidth, maxHeight);
    setBaseDisplaySize(baseSize);

    if (pendingInitialCropRef.current) {
      const saved = pendingInitialCropRef.current;
      const savedRotation = saved.rotation || 0;
      setCropArea(normalizeCropArea(saved.cropArea));
      setRotation(savedRotation);
      setStraighten(saved.straighten || 0);
      syncStageDimensions(baseSize, savedRotation, img.naturalWidth, img.naturalHeight);
      pendingInitialCropRef.current = null;
    } else {
      syncStageDimensions(baseSize, rotation, img.naturalWidth, img.naturalHeight);
    }
  };

  useEffect(() => {
    if (!baseDisplaySize.width || !imageDimensions.naturalWidth) return;
    syncStageDimensions(baseDisplaySize, rotation, imageDimensions.naturalWidth, imageDimensions.naturalHeight);
  }, [rotation, baseDisplaySize, imageDimensions.naturalWidth, imageDimensions.naturalHeight, syncStageDimensions]);

  useEffect(() => {
    updateManualDimensionsFromCropArea();
  }, [cropArea, cropUnit, updateManualDimensionsFromCropArea]);

  const handlePointerDown = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setDragging(handle);
  };

  const handlePointerMove = useCallback((e) => {
    if (!dragging || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    if (dragging === 'move') {
      const deltaX = ((e.clientX - dragStartRef.current.x) / rect.width) * 100;
      const deltaY = ((e.clientY - dragStartRef.current.y) / rect.height) * 100;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      setCropArea((prev) => applyCropResize(prev, 'move', deltaX, deltaY));
      return;
    }

    setCropArea((prev) => applyCropResizeFromPointer(prev, dragging, e.clientX, e.clientY, rect));
  }, [dragging]);

  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => handlePointerMove(e);
    const onUp = () => setDragging(null);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, handlePointerMove]);

  const buildCropMetadata = (exportResult, pageNumber, settings, extra = {}) => {
    const {
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      naturalWidth,
      naturalHeight,
      rotatedWidth,
      rotatedHeight,
      rotationApplied,
    } = exportResult;
    const settingsCrop = settings?.cropArea || cropArea;
    const totalRotation = rotationApplied ?? (settings?.rotation || 0) + (settings?.straighten || 0);
    return {
      document_type: documentCategory,
      source_file_type: sourceFileType,
      crop_unit: cropUnit,
      coordinate_space: 'rotated_display',
      crop_box_inches: {
        top: parseFloat(manualDimensions.top) || 0,
        left: parseFloat(manualDimensions.left) || 0,
        width: parseFloat(manualDimensions.width) || 0,
        height: parseFloat(manualDimensions.height) || 0,
        right: parseFloat(manualDimensions.right) || 0,
        bottom: parseFloat(manualDimensions.bottom) || 0,
      },
      crop_box_percent: {
        x: settingsCrop.x / 100,
        y: settingsCrop.y / 100,
        width: settingsCrop.width / 100,
        height: settingsCrop.height / 100,
      },
      crop_box_pixels: { x: sourceX, y: sourceY, width: sourceWidth, height: sourceHeight },
      natural_image_width: naturalWidth,
      natural_image_height: naturalHeight,
      original_image_width: naturalWidth,
      original_image_height: naturalHeight,
      effective_image_width: rotatedWidth ?? naturalWidth,
      effective_image_height: rotatedHeight ?? naturalHeight,
      display_image_width: imageDimensions.width,
      display_image_height: imageDimensions.height,
      rotation_degrees: totalRotation,
      straighten_degrees: settings?.straighten || 0,
      cropped_mime_type: 'image/jpeg',
      original_file_url: originalFileUrl,
      saved_to_my_documents: saveForLater,
      safety_padding_pct: 0,
      safety_padding_applied: true,
      saved_crop: { x: sourceX, y: sourceY, width: sourceWidth, height: sourceHeight },
      is_full_page: settings?.isFullPage ?? false,
      export_version: Date.now(),
      ...(sourceFileType === 'pdf'
        ? {
            original_pdf_url: originalFileUrl,
            selected_pdf_page_number: pageNumber,
            total_pdf_pages: pdfTotalPages,
            rendered_page_width: pdfRenderedDimensions.width,
            rendered_page_height: pdfRenderedDimensions.height,
          }
        : {}),
      ...extra,
    };
  };

  const originalFileForUpload = uploadSourceFile || uploadedFile;

  const exportAllSelectedPages = async (finalSettings) => {
    const canvases = [];
    const perPageMeta = [];

    for (const pageNumber of selectedPages) {
      const settings = finalSettings[pageNumber] || {
        cropArea: FULL_PAGE_CROP,
        rotation: 0,
        straighten: 0,
        isFullPage: true,
      };
      const result = await renderPdfPageToCanvas(uploadedFile, pageNumber, 2);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = result.imageUrl;
      });
      const exported = await exportCroppedImageBlob(img, settings.cropArea, {
        rotation: settings.rotation,
        straighten: settings.straighten,
      });
      canvases.push(exported.canvas);
      perPageMeta.push({
        pageNumber,
        settings,
        exportResult: exported,
      });
    }

    const baseName = uploadedFile.name.replace(/\.[^/.]+$/, '');
    const allFullPage = perPageMeta.every((p) => p.settings.isFullPage);

    if (allFullPage && selectedPages.length > 1) {
      const batch = [];
      for (const p of perPageMeta) {
        const blob = await new Promise((resolve) => {
          p.exportResult.canvas.toBlob((b) => resolve(b), 'image/jpeg', CROP_EXPORT_JPEG_QUALITY);
        });
        const croppedUrl = URL.createObjectURL(blob);
        const file = createCroppedUploadFile(
          blob,
          `${baseName}_page_${p.pageNumber}_cropped.jpg`,
          'image/jpeg'
        );
        batch.push({
          croppedFile: file,
          cropMetadata: buildCropMetadata(p.exportResult, p.pageNumber, p.settings, {
            cropped_url: croppedUrl,
            selected_pdf_pages: [p.pageNumber],
            is_multi_page_pdf: false,
            combined_page_count: 1,
          }),
        });
      }
      const [first, ...rest] = batch;
      onCropComplete(first.croppedFile, originalFileForUpload, saveForLater, first.cropMetadata, {
        batchAttachments: rest,
      });
      return;
    }

    const combined = await combineCanvasesVertically(canvases);
    const suffix = selectedPages.length > 1 ? `_pages_${formatPageList(selectedPages).replace(/,\s*/g, '-')}` : `_page_${selectedPages[0]}`;
    const croppedFile = createCroppedUploadFile(
      combined.blob,
      `${baseName}${suffix}_cropped.jpg`,
      'image/jpeg'
    );
    const croppedUrl = URL.createObjectURL(combined.blob);

    const primary = perPageMeta[0];
    const cropMetadata = buildCropMetadata(
      primary.exportResult,
      primary.pageNumber,
      primary.settings,
      {
        cropped_url: croppedUrl,
        selected_pdf_pages: selectedPages,
        is_multi_page_pdf: selectedPages.length > 1,
        combined_page_count: selectedPages.length,
        per_page_crop_settings: perPageMeta.map((p) => ({
          page: p.pageNumber,
          crop_box_percent: {
            x: p.settings.cropArea.x / 100,
            y: p.settings.cropArea.y / 100,
            width: p.settings.cropArea.width / 100,
            height: p.settings.cropArea.height / 100,
          },
          is_full_page: p.settings.isFullPage,
        })),
      }
    );

    onCropComplete(croppedFile, originalFileForUpload, saveForLater, cropMetadata);
  };

  const finishSinglePageCrop = async (settingsOverride = null) => {
    if (!imageRef.current) {
      toast.error('Image not loaded');
      return;
    }
    setCropping(true);
    try {
      const settings = settingsOverride || {
        cropArea,
        rotation,
        straighten,
        isFullPage:
          cropArea.x === 0 && cropArea.y === 0 && cropArea.width === 100 && cropArea.height === 100,
      };

      if (isPdf && selectedPages.length > 1) {
        const mergedSettings = { ...pageCropSettings, [currentPageNumber]: settings };
        if (currentCropIndex < selectedPages.length - 1) {
          setPageCropSettings(mergedSettings);
          const nextIndex = currentCropIndex + 1;
          setCurrentCropIndex(nextIndex);
          applyPageCropSettings(selectedPages[nextIndex]);
          await renderPdfPage(selectedPages[nextIndex]);
          toast.info(`Now crop page ${selectedPages[nextIndex]} of ${formatPageList(selectedPages)}`);
          setCropping(false);
          return;
        }
        await exportAllSelectedPages({ ...mergedSettings, [currentPageNumber]: settings });
        setCropping(false);
        return;
      }

      const exported = await exportCroppedImageBlob(imageRef.current, settings.cropArea, {
        rotation: settings.rotation,
        straighten: settings.straighten,
      });

      const baseName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      const croppedFile = createCroppedUploadFile(exported.blob, `${baseName}_cropped.jpg`, 'image/jpeg');
      const croppedUrl = URL.createObjectURL(exported.blob);
      const cropMetadata = buildCropMetadata(
        exported,
        isPdf ? currentPageNumber : 1,
        settings,
        {
          cropped_url: croppedUrl,
          preview_url: croppedUrl,
          preview_cache_key: Date.now(),
          selected_pdf_pages: isPdf ? selectedPages : undefined,
          is_multi_page_pdf: false,
          combined_page_count: 1,
        }
      );
      onCropComplete(croppedFile, originalFileForUpload, saveForLater, cropMetadata);
    } catch (err) {
      toast.error(`Failed to crop: ${err.message}`);
    } finally {
      setCropping(false);
    }
  };

  const handleApplyCrop = () => {
    if (isCropAreaUnusuallySmall(cropArea)) {
      setPendingCropAction('apply');
      setShowSmallCropConfirm(true);
      return;
    }
    saveCurrentPageCropSettings();
    finishSinglePageCrop();
  };

  const handleConfirmSmallCrop = () => {
    setShowSmallCropConfirm(false);
    if (pendingCropAction === 'apply') {
      saveCurrentPageCropSettings();
      finishSinglePageCrop();
    }
    setPendingCropAction(null);
  };

  const goToPrevCropPage = async () => {
    if (currentCropIndex <= 0) return;
    saveCurrentPageCropSettings();
    const nextIndex = currentCropIndex - 1;
    setCurrentCropIndex(nextIndex);
    applyPageCropSettings(selectedPages[nextIndex]);
    await renderPdfPage(selectedPages[nextIndex]);
  };

  const goToNextCropPage = async () => {
    if (currentCropIndex >= selectedPages.length - 1) return;
    saveCurrentPageCropSettings();
    const nextIndex = currentCropIndex + 1;
    setCurrentCropIndex(nextIndex);
    applyPageCropSettings(selectedPages[nextIndex]);
    await renderPdfPage(selectedPages[nextIndex]);
  };

  const sourceTypeLabel = sourceFileType === 'pdf' ? 'PDF' : sourceFileType === 'heic' ? 'HEIC (converted)' : 'Image';
  const overlayPanels = buildCropOverlayPanels(cropArea);
  const { stageWidth, stageHeight } = getStageDimensions(
    baseDisplaySize.width,
    baseDisplaySize.height,
    rotation,
  );
  const totalRotation = rotation + straighten;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto [&>div]:overflow-x-visible dark:bg-[rgba(10,38,28,0.98)] dark:border-[rgba(142,218,182,0.20)] bg-white" data-testid="auto-cropper-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-[#EAF7F0] text-stone-800">
            <Crop size={20} className="text-emerald-500" />
            {workflowPhase === 'pageSelection' && isPdf ? 'Select PDF Pages' : 'Crop Document'}
            {sourceFileType !== 'image' && (
              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                {sourceTypeLabel}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="dark:text-[#93AE9F] text-stone-500">
            {workflowPhase === 'pageSelection'
              ? 'Choose which PDF pages to attach before cropping.'
              : 'Adjust the crop area to remove extra blank space, mostly at the top and bottom.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-emerald-500 mr-3" />
              <span className="text-stone-600 dark:text-[#C7DDD0]">Loading...</span>
            </div>
          )}

          {loadError && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-red-700 dark:text-red-400">{loadError}</p>
                  <Button variant="outline" size="sm" onClick={onClose} className="mt-3">Close</Button>
                </div>
              </div>
            </div>
          )}

          {!loading && !loadError && workflowPhase === 'pageSelection' && isPdf && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">
                  Select only the pages needed as proof. Removing unnecessary pages can reduce printing and mailing cost.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border border-amber-200 dark:border-[rgba(245,158,11,0.25)]">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Do not remove pages that contain your name, address, dates, account details, ID details, or required proof information.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0]">Page range (optional)</label>
                <input
                  type="text"
                  value={customPageRange}
                  onChange={handleCustomPageRangeChange}
                  onBlur={handleCustomRangeBlur}
                  placeholder="1-3, 5, 8-10"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.60)] text-stone-800 dark:text-[#EAF7F0]"
                />
                <p className="mt-1 text-xs text-stone-500 dark:text-[#93AE9F]">Examples: 1-3, 5, 8-10</p>
              </div>

              {pageSelectionError && (
                <p className="text-sm text-red-600 dark:text-red-400">{pageSelectionError}</p>
              )}

              <div>
                <p className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0] mb-2">
                  Click thumbnails or checkboxes to select pages ({selectedPages.length} selected)
                </p>
                {thumbnailsLoading && !pdfThumbnails[1] ? (
                  <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 size={16} className="animate-spin" /> Loading thumbnails...</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                    {Array.from({ length: pdfTotalPages }, (_, i) => i + 1).map((page) => {
                      const selected = selectedPages.includes(page);
                      return (
                        <button
                          key={page}
                          type="button"
                          onClick={() => toggleThumbnailPage(page)}
                          onMouseEnter={() => ensurePdfThumbnail(page)}
                          onFocus={() => ensurePdfThumbnail(page)}
                          className={`relative rounded-lg border overflow-hidden transition-colors ${
                            selected
                              ? 'border-emerald-500 ring-2 ring-emerald-500/40'
                              : 'border-stone-200 dark:border-[rgba(142,218,182,0.20)]'
                          }`}
                          data-testid={`pdf-page-thumb-${page}`}
                          aria-pressed={selected}
                        >
                          {pdfThumbnails[page] ? (
                            <img src={pdfThumbnails[page]} alt={`Page ${page}`} className="w-full h-24 object-cover bg-white" />
                          ) : (
                            <div className="w-full h-24 flex items-center justify-center bg-stone-100 dark:bg-stone-800 text-xs">Page {page}</div>
                          )}
                          <span
                            className={`absolute top-1.5 left-1.5 w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selected
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : 'bg-white/90 dark:bg-stone-900/90 border-stone-300 dark:border-stone-500'
                            }`}
                            data-testid={`pdf-page-checkbox-${page}`}
                          >
                            {selected && <Check size={12} strokeWidth={3} />}
                          </span>
                          <span className="absolute bottom-1 right-1 text-[10px] px-1 rounded bg-black/60 text-white">{page}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && !loadError && workflowPhase === 'cropping' && processedImageUrl && (
            <>
              <div className="mb-4 p-4 rounded-xl bg-amber-50 dark:bg-[rgba(245,158,11,0.12)] border border-amber-200 dark:border-[rgba(245,158,11,0.25)]">
                <div className="flex items-start gap-3">
                  <Scissors size={20} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="space-y-2 text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
                    <p>
                      Crop only extra blank space, mostly top and bottom empty space. This can reduce unnecessary pages and mailing cost.
                    </p>
                    <p>
                      <strong>Do not crop out your name, address, dates, ID details, account details, or required proof information.</strong>
                    </p>
                    {isIdCategory && (
                      <p>You may crop around the ID/card itself, but keep the full ID visible — including name, photo, ID number, expiration date, address, barcode, signature, and edges if needed.</p>
                    )}
                    <p className="text-xs text-amber-600/90 dark:text-amber-300/90">
                      Some proof documents need the full page to be accepted. Use Full Page when the whole document is needed as proof.
                      For full-page proofs with tables, stamps, headers, dates, or certification text, keep the full page or crop only top/bottom blank space carefully.
                    </p>
                  </div>
                </div>
              </div>

              {isPdf && selectedPageCount > 1 && (
                <div className="mb-4 flex items-center justify-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <FileText size={16} className="text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-700 dark:text-blue-400">
                    Page {currentCropIndex + 1} of {selectedPageCount} selected
                    <span className="text-blue-500 dark:text-blue-300 ml-1">(PDF page {currentPageNumber})</span>
                  </span>
                  <Button variant="outline" size="sm" onClick={goToPrevCropPage} disabled={currentCropIndex <= 0} className="h-8 w-8 p-0">
                    <ChevronLeft size={16} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToNextCropPage} disabled={currentCropIndex >= selectedPageCount - 1} className="h-8 w-8 p-0">
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}

              {showSmallCropConfirm && (
                <div className="mb-4 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                    This crop may remove required proof details. Please confirm before continuing.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setShowSmallCropConfirm(false); setPendingCropAction(null); }}>
                      Go Back and Adjust Crop
                    </Button>
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleConfirmSmallCrop}>
                      Continue Anyway
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <div
                    ref={containerRef}
                    className="relative bg-stone-800 dark:bg-[rgba(10,38,28,0.50)] rounded-xl overflow-auto select-none flex items-start justify-center p-4 crop-editor-viewport"
                    style={{ minHeight: '350px', maxHeight: 'calc(100vh - 280px)' }}
                    data-testid="crop-editor-viewport"
                  >
                    <div
                      ref={stageRef}
                      className="relative inline-block mx-auto crop-editor-stage"
                      style={{
                        width: stageWidth ? `${stageWidth}px` : undefined,
                        height: stageHeight ? `${stageHeight}px` : undefined,
                        maxWidth: '100%',
                        lineHeight: 0,
                      }}
                    >
                      <img
                        ref={imageRef}
                        src={processedImageUrl}
                        alt="Document preview"
                        className="block object-contain crop-editor-image"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          width: baseDisplaySize.width || 'auto',
                          height: baseDisplaySize.height || 'auto',
                          maxWidth: baseDisplaySize.width ? 'none' : '100%',
                          maxHeight: baseDisplaySize.width ? 'none' : 'min(70vh, 560px)',
                          transform: `translate(-50%, -50%) rotate(${totalRotation}deg)`,
                          transformOrigin: 'center center',
                        }}
                        crossOrigin="anonymous"
                        draggable={false}
                        onLoad={handleImageLoad}
                      />
                      {overlayPanels.length > 0 && (
                        <div
                          className="absolute inset-0 z-10 pointer-events-none crop-editor-mask"
                          aria-hidden="true"
                        >
                          {overlayPanels.map((panel) => (
                            <div
                              key={panel.key}
                              className="absolute bg-black/50 crop-editor-overlay"
                              style={{
                                left: `${panel.left}%`,
                                top: `${panel.top}%`,
                                width: `${panel.width}%`,
                                height: `${panel.height}%`,
                              }}
                            />
                          ))}
                        </div>
                      )}
                      <div
                        ref={cropBoxRef}
                        className="absolute z-20 border-2 border-emerald-500 pointer-events-none crop-editor-crop-box"
                        style={{
                          left: `${cropArea.x}%`,
                          top: `${cropArea.y}%`,
                          width: `${cropArea.width}%`,
                          height: `${cropArea.height}%`,
                        }}
                        data-testid="crop-box"
                      >
                        <div
                          className="absolute inset-0 pointer-events-auto cursor-move crop-editor-drag-surface"
                          onPointerDown={(e) => handlePointerDown(e, 'move')}
                        />
                        <div
                          role="presentation"
                          className="crop-editor-handle crop-editor-handle-nw"
                          onPointerDown={(e) => handlePointerDown(e, 'nw')}
                          data-testid="crop-handle-nw"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-handle crop-editor-handle-ne"
                          onPointerDown={(e) => handlePointerDown(e, 'ne')}
                          data-testid="crop-handle-ne"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-handle crop-editor-handle-sw"
                          onPointerDown={(e) => handlePointerDown(e, 'sw')}
                          data-testid="crop-handle-sw"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-handle crop-editor-handle-se"
                          onPointerDown={(e) => handlePointerDown(e, 'se')}
                          data-testid="crop-handle-se"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-side-handle crop-editor-side-handle-top"
                          onPointerDown={(e) => handlePointerDown(e, 'n')}
                          data-testid="crop-handle-n"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-side-handle crop-editor-side-handle-bottom"
                          onPointerDown={(e) => handlePointerDown(e, 's')}
                          data-testid="crop-handle-s"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-side-handle crop-editor-side-handle-left"
                          onPointerDown={(e) => handlePointerDown(e, 'w')}
                          data-testid="crop-handle-w"
                        />
                        <div
                          role="presentation"
                          className="crop-editor-side-handle crop-editor-side-handle-right"
                          onPointerDown={(e) => handlePointerDown(e, 'e')}
                          data-testid="crop-handle-e"
                        />
                      </div>
                    </div>
                  </div>
                  {isRecrop && (
                    <p className="mt-2 text-xs text-stone-500 dark:text-[#93AE9F]">
                      Editing from the original image. Adjust the crop box or use Reset Crop to start over.
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                    <span className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0]">Unit</span>
                    <div className="flex rounded-lg overflow-hidden border border-stone-200 dark:border-[rgba(142,218,182,0.20)]">
                      <button onClick={() => setCropUnit('inch')} className={`px-3 py-1 text-xs font-medium ${cropUnit === 'inch' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-600 dark:text-[#C7DDD0]'}`}>inch</button>
                      <button onClick={() => setCropUnit('cm')} className={`px-3 py-1 text-xs font-medium ${cropUnit === 'cm' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-600 dark:text-[#C7DDD0]'}`}>cm</button>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                    <h4 className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0] mb-3">Crop Dimensions</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {['top', 'left', 'width', 'height'].map((field) => (
                        <div key={field}>
                          <label className="text-xs text-stone-500 dark:text-[#93AE9F] capitalize">{field}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={manualDimensions[field]}
                            onChange={(e) => handleManualDimensionChange(field, e.target.value)}
                            className="w-full mt-1 px-2 py-1.5 text-sm rounded border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.60)] text-stone-800 dark:text-[#EAF7F0]"
                          />
                        </div>
                      ))}
                    </div>
                    {dimensionWarning && (
                      <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle size={12} />{dimensionWarning}
                      </div>
                    )}
                  </div>

                  <div className="p-3 rounded-lg bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                    <label className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0] mb-2 block">Straighten: {straighten.toFixed(1)}°</label>
                    <input type="range" min={-30} max={30} step={0.5} value={straighten} onChange={(e) => setStraighten(Number(e.target.value))} className="w-full h-2 accent-emerald-500" />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={() => setRotation((r) => r - 90)} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"><RotateCcw size={14} className="mr-1" /> -90°</Button>
                    <Button variant="outline" size="sm" onClick={() => setRotation((r) => r + 90)} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"><RotateCw size={14} className="mr-1" /> +90°</Button>
                    <Button variant="outline" size="sm" onClick={handleResetCrop} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]" data-testid="reset-crop-btn"><RefreshCcw size={14} className="mr-1" /> Reset Crop</Button>
                    <Button variant="outline" size="sm" onClick={handleUseFullPage} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"><Maximize size={14} className="mr-1" /> Fit Full Image</Button>
                  </div>

                  {isPdf && selectedPageCount > 1 && (
                    <Button variant="outline" size="sm" className="w-full dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]" onClick={handleApplyCropToAll}>
                      <Copy size={14} className="mr-1" /> Apply Crop to All Selected Pages
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-3 p-3 rounded-lg bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                <span className="text-sm text-stone-700 dark:text-[#EAF7F0]">Save cropped copy for later in My Documents</span>
                <button type="button" onClick={() => setSaveForLater(!saveForLater)} className="p-1">
                  {saveForLater ? <ToggleRight size={28} className="text-emerald-500" /> : <ToggleLeft size={28} className="text-stone-400 dark:text-[#93AE9F]" />}
                </button>
              </div>
            </>
          )}
        </div>

        {!loading && !loadError && workflowPhase === 'pageSelection' && isPdf && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">Cancel</Button>
            <Button
              onClick={handleContinueToCrop}
              disabled={selectedPages.length === 0}
              className="text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:pointer-events-none"
              data-testid="continue-to-crop-btn"
            >
              Continue to Crop
            </Button>
          </DialogFooter>
        )}

        {!loading && !loadError && workflowPhase === 'cropping' && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleResetCrop} disabled={cropping} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0] border-stone-200" data-testid="footer-reset-crop-btn">
              <RefreshCcw size={14} className="mr-2" /> Reset Crop
            </Button>
            <Button variant="outline" onClick={handleUseFullPage} disabled={cropping} className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0] border-stone-200">
              <Maximize size={14} className="mr-2" /> Fit Full Image
            </Button>
            <Button onClick={handleApplyCrop} disabled={cropping || !processedImageUrl} className="text-white bg-emerald-600 hover:bg-emerald-700" data-testid="apply-crop-btn">
              {cropping ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
              {isPdf && selectedPageCount > 1 && currentCropIndex < selectedPageCount - 1 ? 'Apply & Next Page' : 'Apply Crop'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedAutoCropperModal;
export { EnhancedAutoCropperModal, FULL_PAGE_CROP };
